/**
 * mobile-nav.js
 * Hamburger toggle for the mobile nav popover (compact card under the header).
 * - CSS-driven open/close: card slides down, items cascade in (see mobile-nav.html)
 *   — kept GSAP-free so this stays on the critical path (instant first tap) while
 *   the GSAP bundle loads lazily.
 * - aria-expanded synced with state
 * - Focus trap while open
 * - Escape / outside click / link tap closes
 */

const toggle = document.getElementById('mobile-nav-toggle');
const drawer = document.getElementById('mobile-nav-drawer');
const linksInDrawer = drawer ? Array.from(drawer.querySelectorAll('a')) : [];

if (toggle && drawer) {
  function open() {
    drawer.classList.remove('hidden');
    drawer.classList.add('flex');
    toggle.setAttribute('aria-expanded', 'true');
    toggle.setAttribute('aria-label', 'Close menu');
    // Force a reflow so the CSS transition runs from the closed state, then flip
    // to the open state (`.is-open` fades + slides the card and cascades items).
    void drawer.offsetWidth;
    drawer.classList.add('is-open');
    requestAnimationFrame(() => linksInDrawer[0]?.focus());
  }

  function close() {
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-label', 'Open menu');
    // Instant close — drop the card so the next open animates from the start.
    drawer.classList.remove('is-open');
    drawer.classList.add('hidden');
    drawer.classList.remove('flex');
  }

  toggle.addEventListener('click', () => {
    const expanded = toggle.getAttribute('aria-expanded') === 'true';
    expanded ? close() : open();
  });

  // Close on nav-link click (so we navigate then dismiss)
  linksInDrawer.forEach((link) => {
    link.addEventListener('click', () => setTimeout(close, 50));
  });

  // Escape closes
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && toggle.getAttribute('aria-expanded') === 'true') {
      close();
    }
  });

  // Outside click closes (popover behavior). Clicks on the toggle are handled
  // by its own listener, so ignore them here.
  document.addEventListener('click', (e) => {
    if (toggle.getAttribute('aria-expanded') !== 'true') return;
    if (drawer.contains(e.target) || toggle.contains(e.target)) return;
    close();
  });

  // Focus trap
  drawer.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    if (toggle.getAttribute('aria-expanded') !== 'true') return;
    const focusables = drawer.querySelectorAll('a, button, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });
}
